/**
 * Badge carousel on analytics.
 */
import React, { useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import { Grid, Box } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { useParams } from "react-router-dom";

import BBadge from "components/BBadge";
import BEmpty from "components/BEmpty";
import { useAsterController } from "context";
import { VText } from "form";
import { mapSectionToGradeDetails } from "utils/grade.util";

const responsive = {
  desktop: {
    breakpoint: { max: 2048, min: 1024 },
    items: 3,
    slidesToSlide: 2 // optional, default to 1.
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
    slidesToSlide: 2 // optional, default to 1.
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1 // optional, default to 1.
  }
};

/**
 * Responsive carousel of module badges earned in the course.
 */
const AnalysisBadge = () => {

  const { t } = useTranslation('common');

  const { courseId } = useParams();

  const [controller] = useAsterController();
  const { miniSidenav, mobile, courses, loadedCourseDetail } = controller;

  const currCourse = useMemo(() => (courses.data || []).find(v => v.id === courseId) || {}, [courses, courseId]);

  const [show, setShow] = useState(true);
  const [modules, setModules] = useState([]);

  useEffect(() => { if (!mobile) { setShow(false); setTimeout(() => setShow(true), 300); } }, [miniSidenav, mobile]);

  useEffect(() => {
    const mapper = mapSectionToGradeDetails(currCourse).filter(m => m['details'] && m['cutoff'] !== 0 && m.gradeFormat?.toLowerCase() === 'quiz');

    const modules = (currCourse.moduleMapper || []).filter(v => v.gradeFormat?.toLowerCase() === 'quiz').map(v => {
      const item = mapper.find(m => m.sectionTitle === v.sectionTitle);
      return {
        key: v.sectionTitle,
        title: v.sectionTitle,
        earned: item && item.raw_possible ? item.raw_earned / item.raw_possible > item.cutoff : false
      };
    });

    setModules(modules);

  }, [currCourse, currCourse.id]);

  return <>
    <Grid container spacing={3} className="mt-6">
      <Grid item sm={12} md={4} className="flex">
        <VText className="text-2xl">{t("Unlocked Badges")}</VText>
      </Grid>
    </Grid>

    <Box className="flex justify-center mt-8">
      {
        loadedCourseDetail ?
          <Box className="w-full v-carousel">
            {
              modules.length ? <>
                {
                  show && <Carousel
                    swipeable={true}
                    draggable={true}
                    showDots={true}
                    arrows={false}
                    responsive={responsive}
                    ssr={true}
                    infinite={false}
                    autoPlay={false}
                    autoPlaySpeed={1000}
                    // customButtonGroup={<ButtonGroup />}
                    renderButtonGroupOutside={true}
                    keyBoardControl={true}
                    customTransition="all .5s"
                    transitionDuration={500}
                    containerClass="w-full"
                    removeArrowOnDeviceType={["tablet", "mobile"]}
                    renderDotsOutside
                  >
                    {
                      modules.map((v, idx) => <Box key={v.key} className=" flex justify-center relative">
                        <Box className={`w-full h-0.5 bg-red-500 z-0 ${idx === 0 ? 'invisible' : 'visible'}`} sx={{ marginTop: '65px' }} />
                        <Box className="flex-full relative">
                          <div className={v.earned ? '' : 'v-filter-gray'}>
                            <BBadge index={idx + 1} className="w-32" />
                          </div>
                        </Box>
                        <Box className={`w-full h-0.5 bg-red-500 ${idx === modules.length - 1 ? 'invisible' : 'visible'}`} sx={{ marginTop: '65px' }} />
                        <Box className={`w-9 h-9 absolute ${idx === modules.length - 1 ? 'invisible' : 'visible'}`} sx={{ right: '-18px', top: '47px' }}>
                          <Box className="w-9 h-9 flex justify-center items-center border-2 border-solid border-red-500 dark:border-white rounded-full relative v-bg-body dark:bg-gray-800">
                            {
                              v.earned ?
                                <>
                                  <Icon className="text-gray-500 dark:text-white text-2xl" icon="bi:check-lg" />
                                  <VText className="absolute" style={{ top: '40px' }}>{t('Done')}</VText>
                                </>
                                :
                                <>
                                  <Icon className="text-gray-500 dark:text-white text-2xl" icon="ic:outline-timer" />
                                  <VText className="absolute" style={{ top: '40px' }}>{t('Progress')}</VText>
                                </>
                            }
                          </Box>
                        </Box>
                      </Box>)
                    }
                  </Carousel>
                }
              </> :
                <BEmpty />
            }

          </Box>
          :
          <Skeleton containerClassName="w-full" className="w-full h-16 mt-2" count={3} />
      }

    </Box>

  </>;
};

export default AnalysisBadge;